/**
 * AppointmentsWebLeads — pestaña «Solicitudes web» (doc 0398 §4.4)
 *
 * Lo que llega del formulario de la web del cliente. NO son citas: no tienen
 * hora, ni profesional, ni han pasado por ninguna comprobación de
 * disponibilidad. Convertirlas en cita es una fase posterior — tocaría el motor
 * que ahora mismo está en refactor (0396).
 *
 * POR QUÉ SE REFRESCA SOLO
 *
 * La demostración consiste en que el cliente pulse Enviar en su propia web y vea
 * la solicitud aparecer. Si hay que darle a un botón para que aparezca, el
 * efecto se pierde: parece que el panel la fue a buscar, no que llegó. El
 * refresco automático se pausa cuando la pestaña del navegador no está visible,
 * para no consultar cada 10 segundos contra un portátil olvidado.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import BlockIcon from "@material-ui/icons/Block";
import CodeIcon from "@material-ui/icons/Code";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PersonIcon from "@material-ui/icons/Person";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import moment from "moment";
import { toast } from "react-toastify";
import { useStyles } from "./styles";
import { softTone } from "../../theme/agendaTheme";
import { listWebLeads, updateWebLeadStatus, deleteWebLead } from "../../services/webLeads.api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ContactProfile360Modal from "../../components/ContactProfile360Modal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const REFRESH_MS = 10000;

/**
 * Colores propios, deliberadamente NO añadidos a `AGENDA_STATUS_COLORS`: ese
 * mapa es la fuente única de los estados de una CITA, y mezclar ahí los de una
 * solicitud web haría creer que son el mismo ciclo de vida. No lo son.
 */
const WEB_LEAD_COLORS = {
  nueva: "#1976d2",
  vista: "#2e7d32",
  descartada: "#757575",
};

export default function AppointmentsWebLeads() {
  const classes = useStyles();
  const theme = useTheme();
  const agendaMode = (theme.agenda && theme.agenda.mode) || theme.palette.type;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  // Mismo criterio de elevación que ya usa el menú lateral
  // (`AccordionMenu.js`) — el borrado real (0421) es la única acción
  // destructiva de verdad en esta pantalla, así que se gatea igual de
  // estricto que las secciones de administración del propio menú.
  const isAdmin =
    user?.profile === "admin" ||
    user?.super === true ||
    user?.super === 1 ||
    String(user?.super) === "1";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [payloadOf, setPayloadOf] = useState(null);
  const [profileContactId, setProfileContactId] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: "", label: i18n.t("appointmentsModule.webLeads.filterAll") },
      { value: "nueva", label: i18n.t("appointmentsModule.webLeads.statusNueva") },
      { value: "vista", label: i18n.t("appointmentsModule.webLeads.statusVista") },
      { value: "descartada", label: i18n.t("appointmentsModule.webLeads.statusDescartada") },
    ],
    [i18n.language],
  );

  // `silent` evita el parpadeo del refresco automático: solo la carga que pide
  // el usuario muestra el estado de espera.
  const fetchLeads = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      try {
        const { data } = await listWebLeads({ status: status || undefined, page, pageSize: rowsPerPage });
        if (!isMountedRef.current) return;
        setRows(data?.rows || []);
        setTotal(data?.total || 0);
        setNewCount(data?.newCount || 0);
      } catch (e) {
        if (!silent) toastError(e);
      } finally {
        if (isMountedRef.current && !silent) setLoading(false);
      }
    },
    [status, page, rowsPerPage],
  );

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") fetchLeads({ silent: true });
    };
    const timer = setInterval(tick, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchLeads]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateWebLeadStatus(id, newStatus);
      fetchLeads({ silent: true });
    } catch (e) {
      toastError(e);
    }
  };

  // Borrado REAL (0421) — distinto de "Descartar": no deja rastro. Pensado
  // para limpiar datos de PRUEBA, nunca para un lead real que no prosperó
  // (ese caso sigue siendo "Descartar", que sí conserva el historial).
  const handleDelete = async () => {
    if (!deletingLead) return;
    try {
      await deleteWebLead(deletingLead.id);
      toast.success(i18n.t("appointmentsModule.webLeads.deleteSuccess"));
      fetchLeads({ silent: true });
    } catch (e) {
      toastError(e);
    } finally {
      setDeletingLead(null);
    }
  };

  const pillStyle = (s) => softTone(WEB_LEAD_COLORS[s] || WEB_LEAD_COLORS.descartada, agendaMode);
  const statusLabel = (s) =>
    i18n.t(`appointmentsModule.webLeads.status${s.charAt(0).toUpperCase()}${s.slice(1)}`, { defaultValue: s });

  /**
   * Cuatro estados, nunca confundidos entre sí (corregido 2026-09-21: el
   * propietario probó un número inventado y el check salía verde igual —
   * el diseño anterior solo distinguía DOS motivos de "no válido" y trataba
   * todo lo demás como "válido", cuando en realidad "el formato no falló"
   * y "confirmado en WhatsApp" nunca fueron lo mismo):
   *
   *   1. `phoneE164` nulo — problema de FORMATO, ni se pudo interpretar.
   *   2. `hasWhatsapp === false` — WhatsApp real confirmó que esa cuenta
   *      NO existe (`numberChecker.check()`, corrido siempre desde esta
   *      mejora, incluso en modo seguro).
   *   3. `hasWhatsapp === true` — WhatsApp real CONFIRMÓ que sí existe.
   *   4. `hasWhatsapp` es `null`/`undefined` — formato aceptado, pero
   *      TODAVÍA sin verificar contra la red real (el job de conversión no
   *      ha corrido, o está a medio camino). Ícono neutral, nunca un check
   *      verde: afirmar algo que no se sabe es peor que no afirmar nada.
   */
  const whatsappStatus = (lead) => {
    if (!lead.phoneE164) {
      return { state: "invalid_format", ok: false, label: i18n.t("appointmentsModule.webLeads.phoneInvalidFormat") };
    }
    if (lead.hasWhatsapp === false) {
      return { state: "no_whatsapp", ok: false, label: i18n.t("appointmentsModule.webLeads.phoneNoWhatsapp") };
    }
    if (lead.hasWhatsapp === true) {
      return { state: "confirmed", ok: true, label: i18n.t("appointmentsModule.webLeads.phoneConfirmedWhatsapp") };
    }
    return { state: "unchecked", ok: null, label: i18n.t("appointmentsModule.webLeads.phoneUnchecked") };
  };

  // Solo se tiñe la fila ante un problema CONFIRMADO (`ok === false`) —
  // "todavía sin verificar" (`ok === null`) no es un problema, es falta de
  // información, y teñirla igual asustaría sin motivo.
  const invalidRowTint = (lead) =>
    whatsappStatus(lead).ok === false ? { backgroundColor: softTone("#d32f2f", agendaMode).backgroundColor } : undefined;

  const phoneCell = (lead) => {
    const wa = whatsappStatus(lead);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span>{lead.phoneE164 ? `+${lead.phoneE164}` : lead.phoneRaw}</span>
        <Tooltip title={wa.label}>
          {wa.ok === true ? (
            <CheckCircleIcon style={{ color: "#2e7d32" }} fontSize="small" />
          ) : wa.ok === false ? (
            <CancelIcon style={{ color: "#d32f2f" }} fontSize="small" />
          ) : (
            <HelpOutlineIcon style={{ color: "#9e9e9e" }} fontSize="small" />
          )}
        </Tooltip>
      </div>
    );
  };

  /**
   * «hace 4 segundos» y no una hora absoluta: los minutos los calcula el
   * servidor (`ageMinutes`), que es donde están los dos extremos de la resta. El
   * reloj del portátil del cliente no participa — y en una demostración en vivo,
   * ese reloj nunca coincide.
   */
  const receivedLabel = (lead) => {
    if (lead.ageMinutes < 1) return i18n.t("appointmentsModule.webLeads.justNow");
    if (lead.ageMinutes < 60)
      return i18n.t("appointmentsModule.webLeads.minutesAgo", { count: lead.ageMinutes });
    // `receivedAt` llega como ISO-8601 con `Z`: es un instante en UTC, porque la
    // conexión del backend trabaja en UTC aunque el servidor vaya en UTC−5
    // (doc 0397). Moment lo pasa a la hora de quien mira. Parsearlo con formato
    // explícito, como si fuera hora local, mostraría cinco horas de más.
    return moment(lead.receivedAt).format("L LT");
  };

  const formatPreferred = (value) =>
    value ? moment(value, "YYYY-MM-DD").format("L") : "—";

  const renderActions = (lead) => (
    <>
      {/* Modo seguro (0417, Punto 4/6): sin ticket, el único rastro de la
          conversión es el Contacto vinculado — mismo panel 360 que ya usa
          la página Contactos, para no construir una segunda vista de lo
          mismo. Solo aparece si de verdad hay un contacto que mostrar. */}
      {lead.contactId && (
        <Tooltip title={i18n.t("appointmentsModule.webLeads.seeContact")}>
          <IconButton
            size="small"
            onClick={() => setProfileContactId(lead.contactId)}
            aria-label={i18n.t("appointmentsModule.webLeads.seeContact")}
          >
            <PersonIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={i18n.t("appointmentsModule.webLeads.markSeen")}>
        <span>
          <IconButton
            size="small"
            disabled={lead.status === "vista"}
            onClick={() => handleStatusChange(lead.id, "vista")}
            aria-label={i18n.t("appointmentsModule.webLeads.markSeen")}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={i18n.t("appointmentsModule.webLeads.discard")}>
        <span>
          <IconButton
            size="small"
            disabled={lead.status === "descartada"}
            onClick={() => handleStatusChange(lead.id, "descartada")}
            aria-label={i18n.t("appointmentsModule.webLeads.discard")}
          >
            <BlockIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={i18n.t("appointmentsModule.webLeads.seePayload")}>
        <IconButton
          size="small"
          onClick={() => setPayloadOf(lead)}
          aria-label={i18n.t("appointmentsModule.webLeads.seePayload")}
        >
          <CodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {/* Borrado real (0421) — solo admin/super, mismo criterio que ya
          exige el backend en la ruta. Distinto de "Descartar": esto SÍ
          borra la fila, pensado para limpiar datos de prueba. */}
      {isAdmin && (
        <Tooltip title={i18n.t("appointmentsModule.webLeads.deleteLead")}>
          <IconButton
            size="small"
            onClick={() => setDeletingLead(lead)}
            aria-label={i18n.t("appointmentsModule.webLeads.deleteLead")}
          >
            <DeleteForeverIcon fontSize="small" style={{ color: "#d32f2f" }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
      <div>
        <Typography variant="body2" color="textSecondary">
          {i18n.t("appointmentsModule.webLeads.countLine", { count: total, pending: newCount })}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("appointmentsModule.webLeads.autoRefreshHint")}
        </Typography>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* `displayEmpty`: sin él, MUI trata la cadena vacía como «nada
            seleccionado» y deja el desplegable en blanco en vez de mostrar
            «Todas», que es justo el filtro activo al entrar. */}
        <TextField
          select
          size="small"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          style={{ minWidth: 150 }}
          SelectProps={{ displayEmpty: true }}
        >
          {statusOptions.map((o) => (
            <MenuItem key={o.value || "all"} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => fetchLeads()}
        >
          {i18n.t("appointmentsModule.webLeads.reload")}
        </Button>
      </div>
    </div>
  );

  const pagination = (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage={i18n.t("appointmentsModule.historial.rowsPerPage")}
      labelDisplayedRows={({ from, to, count }) =>
        i18n.t("appointmentsModule.historial.displayedRows", { from, to, count })
      }
      style={{ marginTop: 8 }}
    />
  );

  const payloadDialog = (
    <Dialog open={!!payloadOf} onClose={() => setPayloadOf(null)} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("appointmentsModule.webLeads.payloadTitle")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("appointmentsModule.webLeads.payloadHint")}
        </Typography>
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.78rem", marginTop: 8 }}>
          {payloadOf ? JSON.stringify(payloadOf.rawPayload, null, 2) : ""}
        </pre>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPayloadOf(null)}>{i18n.t("appointmentsModule.webLeads.close")}</Button>
      </DialogActions>
    </Dialog>
  );

  const contactProfileModal = (
    <ContactProfile360Modal
      open={!!profileContactId}
      onClose={() => setProfileContactId(null)}
      contactId={profileContactId || undefined}
    />
  );

  const deleteConfirmModal = (
    <ConfirmationModal
      title={i18n.t("appointmentsModule.webLeads.deleteConfirmTitle")}
      open={!!deletingLead}
      onClose={() => setDeletingLead(null)}
      onConfirm={handleDelete}
    >
      {i18n.t("appointmentsModule.webLeads.deleteConfirmMessage", {
        name: deletingLead?.fullName || "",
      })}
    </ConfirmationModal>
  );

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Typography color="textSecondary">{i18n.t("appointmentsModule.webLeads.loading")}</Typography>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        {header}
        <div style={{ padding: 24, textAlign: "center" }}>
          <Typography color="textSecondary">{i18n.t("appointmentsModule.webLeads.empty")}</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            {i18n.t("appointmentsModule.webLeads.emptyHint")}
          </Typography>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ padding: 16 }}>
        {header}
        {rows.map((lead) => (
          <Paper key={lead.id} className={classes.historialCard} elevation={0} style={invalidRowTint(lead)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
              <div>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{lead.fullName}</Typography>
                <Typography variant="body2" color="textSecondary" component="div">
                  {phoneCell(lead)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {lead.serviceRequested || "—"} · {formatPreferred(lead.preferredDate)}
                </Typography>
                <Typography variant="caption" color="textSecondary">{receivedLabel(lead)}</Typography>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className={classes.statusPillSm} style={pillStyle(lead.status)}>
                  {statusLabel(lead.status)}
                </span>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>{renderActions(lead)}</div>
          </Paper>
        ))}
        {pagination}
        {payloadDialog}
        {contactProfileModal}
        {deleteConfirmModal}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {header}
      <TableContainer component={Paper} className={classes.historialTableWrap} elevation={0}>
        <Table size="small" stickyHeader aria-label={i18n.t("appointmentsModule.webLeads.tableAria")}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colReceived")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colName")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colPhone")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colPreferred")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colService")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colSource")}</TableCell>
              <TableCell className={classes.historialTh}>{i18n.t("appointmentsModule.webLeads.colStatus")}</TableCell>
              <TableCell className={classes.historialTh} align="right">{i18n.t("appointmentsModule.webLeads.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((lead) => (
              <TableRow key={lead.id} hover className={classes.historialRow} style={invalidRowTint(lead)}>
                <TableCell>{receivedLabel(lead)}</TableCell>
                <TableCell>{lead.fullName}</TableCell>
                {/* Un solo número, nunca los dos apilados: si se pudo
                    normalizar, ES el mismo número, solo que en formato
                    internacional — mostrar los dos era redundancia, no
                    información nueva. El icono dice si hay algún problema
                    conocido, nunca "verificado contra WhatsApp de verdad". */}
                <TableCell>{phoneCell(lead)}</TableCell>
                <TableCell>{formatPreferred(lead.preferredDate)}</TableCell>
                <TableCell>{lead.serviceRequested || "—"}</TableCell>
                <TableCell>{lead.source}</TableCell>
                <TableCell>
                  <span className={classes.statusPill} style={pillStyle(lead.status)}>
                    {statusLabel(lead.status)}
                  </span>
                </TableCell>
                <TableCell align="right">{renderActions(lead)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
      {payloadDialog}
      {contactProfileModal}
      {deleteConfirmModal}
    </div>
  );
}
